import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { clearStoredAuth } from '../lib/supabase';
import { syncLanguageWithUser } from '../i18n';

// Cache auth result for 2 minutes to avoid re-calling api.me() on every navigation
let _authCache = { data: null, hasUnternehmen: false, accountDeletionStatus: 'active', expires: 0 };
const AUTH_CACHE_TTL = 600_000; // 10 minutes

export function invalidateAuthCache() {
  _authCache = { data: null, hasUnternehmen: false, accountDeletionStatus: 'active', expires: 0 };
}

export function markHasUnternehmenInAuthCache(data = null) {
  _authCache = {
    data: data ?? _authCache.data,
    hasUnternehmen: true,
    accountDeletionStatus: _authCache.accountDeletionStatus || 'active',
    expires: Date.now() + AUTH_CACHE_TTL,
  };
}

export default function ProtectedRoute({
  children,
  requireUnternehmen = false,
  redirectIfUnternehmen = false,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [state, setState] = useState(() =>
    _authCache.expires > Date.now() ? 'authenticated' : 'loading'
  );
  const [hasUnternehmen, setHasUnternehmen] = useState(_authCache.hasUnternehmen);
  const [accountDeletionStatus, setAccountDeletionStatus] = useState(
    _authCache.accountDeletionStatus || 'active'
  );
  const cacheIsFresh = _authCache.expires > Date.now();
  const effectiveState = cacheIsFresh ? 'authenticated' : state;
  const effectiveHasUnternehmen = cacheIsFresh ? _authCache.hasUnternehmen : hasUnternehmen;
  const effectiveAccountDeletionStatus = cacheIsFresh
    ? (_authCache.accountDeletionStatus || 'active')
    : accountDeletionStatus;

  useEffect(() => {
    // If cache is still valid, skip the network calls
    if (_authCache.expires > Date.now()) {
      setHasUnternehmen(_authCache.hasUnternehmen);
      setAccountDeletionStatus(_authCache.accountDeletionStatus || 'active');
      setState('authenticated');
      return;
    }

    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          clearStoredAuth();
          setState('unauthenticated');
          return;
        }

        const res = await api.me();
        if (res.success) {
          localStorage.setItem('user', JSON.stringify(res.data.user));
          syncLanguageWithUser(res.data.user);
          const lifecycleStatus = res?.data?.user?.account_deletion_status || 'active';
          setHasUnternehmen(res.data.has_unternehmen);
          setAccountDeletionStatus(lifecycleStatus);
          _authCache = {
            data: res.data,
            hasUnternehmen: res.data.has_unternehmen,
            accountDeletionStatus: lifecycleStatus,
            expires: Date.now() + AUTH_CACHE_TTL,
          };
          setState('authenticated');
        } else {
          setState('unauthenticated');
        }
      } catch {
        clearStoredAuth();
        setState('unauthenticated');
      }
    })();
  }, [location.pathname]);

  if (effectiveState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" aria-label={t('app.loading')}></div>
      </div>
    );
  }

  if (effectiveState === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  const accountIsReadOnly =
    effectiveAccountDeletionStatus === 'read_only' ||
    effectiveAccountDeletionStatus === 'scheduled_for_deletion';

  if (accountIsReadOnly && location.pathname !== '/einstellungen') {
    return <Navigate to="/einstellungen?tab=benutzer" replace />;
  }
  if (accountIsReadOnly && location.pathname === '/einstellungen') {
    return <>{children}</>;
  }

  if (requireUnternehmen && !effectiveHasUnternehmen) {
    return <Navigate to="/onboarding" replace />;
  }

  if (redirectIfUnternehmen && effectiveHasUnternehmen) {
    return <Navigate to="/dashboard" replace />;
  }

  // Subscription gate is temporarily disabled.
  return <>{children}</>;
}
